// Floating value popups and tier banners in the 5×7 pixel font.
import gsap from 'gsap';
import { Container, Sprite } from 'pixi.js';
import type { PaletteKey } from '../../art/palette';
import { textTexture } from '../../art/rasterize';
import { clock } from '../timeline/clock';

export class Popups {
  readonly layer = new Container();

  /** "+34" floating up from a point. */
  float(text: string, x: number, y: number, color: PaletteKey = 'gold', scale = 1, ms = 700): Promise<void> {
    if (clock.isSkipping) return Promise.resolve();
    const s = new Sprite(textTexture(text, color, 'ink', scale));
    s.anchor.set(0.5);
    s.position.set(Math.round(x), Math.round(y));
    this.layer.addChild(s);
    s.scale.set(0.5);
    clock.fx(s.scale, 120, { x: 1, y: 1, ease: 'back.out(3)' });
    return clock.tween(s, ms, { y: y - 18, alpha: 0, ease: 'power1.in', delay: 0.25 }).then(() => {
      if (s.destroyed) return;
      this.layer.removeChild(s);
      s.destroy();
    });
  }

  /** A stamped label that stays until removed. */
  label(text: string, x: number, y: number, color: PaletteKey = 'paper', scale = 1): Sprite {
    const s = new Sprite(textTexture(text, color, 'ink', scale));
    s.anchor.set(0.5);
    s.position.set(Math.round(x), Math.round(y));
    this.layer.addChild(s);
    return s;
  }

  /** Big banner that slides in, holds, and leaves. */
  async banner(text: string, x: number, y: number, color: PaletteKey, scale: number, holdMs: number): Promise<void> {
    if (clock.isSkipping) return;
    const s = new Sprite(textTexture(text, color, 'ink', scale));
    s.anchor.set(0.5);
    s.position.set(x - 400, Math.round(y));
    this.layer.addChild(s);
    await clock.tween(s, 180, { x, ease: 'back.out(1.6)' });
    await clock.wait(holdMs);
    await clock.tween(s, 160, { x: x + 400, alpha: 0, ease: 'power2.in' });
    if (s.destroyed) return;
    this.layer.removeChild(s);
    s.destroy();
  }

  /** Hide everything now; sprites still tweening clean themselves up when their tween ends. */
  clear(): void {
    for (const c of [...this.layer.children]) {
      if (gsap.getTweensOf([c, c.scale]).length) c.visible = false;
      else {
        this.layer.removeChild(c);
        c.destroy();
      }
    }
  }
}
